import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { PHONE_GATE } from "@/constants/testIds/auth";
import { api, formatApiError, phoneSendRequest, phoneVerifyRequest } from "@/lib/api";
import { isValidPhone } from "@/lib/phone";

const PhoneGateContent = ({ initialPhone, onDone, onDefer, onLogout }) => {
  const [phone, setPhone] = useState(initialPhone || "");
  const [hasStoredPhone, setHasStoredPhone] = useState(Boolean(initialPhone));
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone");
  const [resendAfter, setResendAfter] = useState(0);
  const [busy, setBusy] = useState(false);
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    if (initialPhone !== undefined) return undefined;
    let active = true;
    api.get("/members/me").then(({ data }) => {
      if (!active || !data?.phone) return;
      setPhone((current) => current || data.phone);
      setHasStoredPhone(true);
    }).catch(() => undefined);
    return () => {
      active = false;
    };
  }, [initialPhone]);

  useEffect(() => {
    if (resendAfter <= 0) return undefined;
    const timer = window.setInterval(
      () => setResendAfter((value) => Math.max(0, value - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [resendAfter]);

  const showError = (error) => {
    const response = error?.response;
    const detail = response?.data?.detail;
    const errorCode = typeof detail === "string" ? detail : detail?.code;
    if (response?.status === 409 && errorCode === "phone_in_use") {
      setBranch("in-use");
      return;
    }
    if (response?.status === 503 && errorCode === "otp_unavailable") {
      setBranch("unavailable");
      return;
    }
    if (response?.status === 429) {
      const wait = response.data?.retry_after || detail?.retry_after;
      toast.error(wait ? `Please wait ${wait} seconds before trying again` : "Please wait before trying again");
      return;
    }
    if (errorCode === "otp_locked") {
      setStep("phone");
      setCode("");
      toast.error("Too many incorrect attempts. Please request a new code.");
      return;
    }
    const attemptsLeft = response?.data?.attempts_left ?? detail?.attempts_left;
    if (attemptsLeft != null) {
      toast.error(`Incorrect code. ${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left.`);
      return;
    }
    toast.error(formatApiError(error, "Could not verify code"));
  };

  const sendCode = async () => {
    if (!isValidPhone(phone.trim())) {
      toast.error("Enter a valid mobile number");
      return;
    }
    setBusy(true);
    setBranch(null);
    try {
      const data = await phoneSendRequest(phone.trim());
      if (data?.status === "already_verified") {
        onDone();
        return;
      }
      setStep("code");
      setResendAfter(data?.resend_after ?? 45);
      if (data?.dev_otp && process.env.NODE_ENV !== "production") setCode(data.dev_otp);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (step === "phone") {
      await sendCode();
      return;
    }
    setBusy(true);
    setBranch(null);
    try {
      await phoneVerifyRequest(phone.trim(), code);
      onDone();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={PHONE_GATE.dialog}>
      <div className="mb-5 flex items-center gap-3">
        <img src="/logo-mark.png" alt="" className="h-11 w-11" />
        <div className="leading-[1.08]">
          <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-ink">Greentek</span>
          <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-gold">Alliance</span>
        </div>
      </div>
      <h1 className="brand-title text-2xl font-semibold text-brand-ink">
        {hasStoredPhone ? "Confirm your mobile number" : "Add your mobile number"}
      </h1>
      <p className="mt-1.5 text-sm text-brand-muted">
        {step === "phone"
          ? "We will send a six-digit code to your WhatsApp number."
          : `Enter the WhatsApp code sent to ${phone}.`}
      </p>

      {branch === "in-use" ? (
        <div className="mt-6 space-y-4">
          <p className="rounded-[12px] bg-red-50 p-4 text-sm text-red-700">
            This number is on another account
          </p>
        </div>
      ) : branch === "unavailable" ? (
        <div className="mt-6 space-y-4">
          <p className="rounded-[12px] bg-amber-50 p-4 text-sm text-amber-800">
            We could not send the code right now
          </p>
          <Button
            type="button"
            onClick={onDefer}
            data-testid={PHONE_GATE.continueButton}
            className="h-11 w-full rounded-[12px] bg-brand-forest text-white"
          >
            Continue
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="phone-gate-phone" className="text-[13px] font-medium text-brand-ink">
              Mobile number
            </Label>
            <Input
              id="phone-gate-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              required
              maxLength={13}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              disabled={step === "code"}
              placeholder="10-digit mobile number"
              data-testid={PHONE_GATE.phoneInput}
              className="h-11 rounded-[12px] border-black/10 bg-white"
            />
          </div>
          {step === "code" && (
            <div className="space-y-1.5">
              <Label htmlFor="phone-gate-code" className="text-[13px] font-medium text-brand-ink">
                WhatsApp code
              </Label>
              <Input
                id="phone-gate-code"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                data-testid={PHONE_GATE.codeInput}
                className="h-11 rounded-[12px] border-black/10 bg-white"
              />
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid={step === "phone" ? PHONE_GATE.sendButton : PHONE_GATE.verifyButton}
            className="h-11 w-full rounded-[12px] bg-brand-forest font-semibold text-white"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : step === "phone" ? "Send code" : "Verify"}
          </Button>
          {step === "code" && (
            <Button
              type="button"
              variant="ghost"
              disabled={busy || resendAfter > 0}
              onClick={sendCode}
              data-testid={PHONE_GATE.resendButton}
              className="w-full"
            >
              {resendAfter > 0 ? `Resend in ${resendAfter}s` : "Resend code"}
            </Button>
          )}
        </form>
      )}

      <button
        type="button"
        onClick={onLogout}
        data-testid={PHONE_GATE.signOutButton}
        className="mt-4 w-full text-center text-sm font-medium text-brand-gold-deep underline-offset-2 hover:underline"
      >
        {branch === "in-use" ? "Sign out and use the WhatsApp code on that number" : "Sign out"}
      </button>
    </div>
  );
};

export const PhoneGate = ({
  initialPhone,
  onDone,
  onDefer,
  onLogout,
  dismissible = false,
  onDismiss,
}) => {
  const content = (
    <PhoneGateContent
      initialPhone={initialPhone}
      onDone={onDone}
      onDefer={onDefer}
      onLogout={onLogout}
    />
  );

  if (dismissible) {
    return (
      <Dialog open onOpenChange={(open) => !open && onDismiss?.()}>
        <DialogContent className="max-w-[420px] rounded-[20px] p-7">
          <DialogHeader className="sr-only"><DialogTitle>Mobile number</DialogTitle></DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] grid max-h-[100dvh] place-items-center overflow-y-auto bg-brand-canvas/95 px-4 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] backdrop-blur" role="dialog" aria-modal="true">
      <div className="w-full max-w-[420px] rounded-[20px] border border-black/5 bg-white/90 p-7 shadow-xl">
        {content}
      </div>
    </div>
  );
};
